import { useEffect, useRef } from "react";

import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";
import { CommentThreadCard } from "src/components/comments/comment-thread-card";
import { useCommentLink } from "src/components/comments/use-comment-link";
import { useCommentThreads } from "src/components/comments/use-comment-threads";
import { useMentionSearch } from "src/components/comments/use-mention-search";
import {
  formatCommentAnchorTimestamp,
  isThreadResolved,
} from "src/components/comments/utils";

type Props = {
  subject: CommentThreadSubject;
  subtitle?: string | null;
  /** Scopes which review agents can be mentioned. Null when the surface has none. */
  brandProfileId?: string | null;
  threadId: string;
  onDismiss: () => void;
  /** Shows the thread inside a list it was opened from, with a way back to it. */
  onBack?: () => void;
  /** Sits inside another surface, like a popover, so it has no frame of its own. Implied by `onBack`. */
  isEmbedded?: boolean;
  /** Embedded in a surface that sizes to its content, like a popover: caps the card so its conversation scrolls. */
  capHeight?: boolean;
};

/**
 * The thread card anchored beside its pin on the canvas — where reading a
 * thread and replying to it actually happen.
 *
 * Reads through the same hook as the rail, so both render one cache entry and
 * mutate through the same handlers.
 */
export const InlineCommentThread = ({
  subject,
  subtitle,
  brandProfileId,
  threadId,
  onDismiss,
  onBack,
  isEmbedded = onBack !== undefined,
  capHeight,
}: Props) => {
  const searchMentions = useMentionSearch({ brandProfileId });
  const {
    threads,
    toComments,
    reply,
    editComment,
    toggleResolved,
    removeComment,
    removeThread,
    isReplying,
    isSavingEdit,
    markReadOnOpen,
    setThreadRead,
    isUnread,
  } = useCommentThreads({
    subject,
    // The card is open on this thread, so an agent's reply is being read as it
    // lands rather than just collected.
    watchAgentReplies: true,
    onThreadDeleted: onDismiss,
  });

  const thread = threads?.find((candidate) => candidate.id === threadId);
  const { highlightedCommentId, copyCommentLink } = useCommentLink();

  // Opening the card is the acknowledgement, and this mounts for both ways in —
  // the canvas pin and the rail row — so the write lives here rather than at
  // each click site. The ref keeps it to once per thread: markReadOnOpen is
  // rebuilt every render, so depending on it would re-fire on every poll.
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (thread && markedRef.current !== thread.id) {
      markedRef.current = thread.id;
      void markReadOnOpen(thread.id);
    }
  }, [thread, markReadOnOpen]);

  if (!thread) {
    return null;
  }

  const comments = toComments(thread);
  const isResolved = isThreadResolved(thread);

  return (
    <CommentThreadCard
      variant="inline"
      subtitle={subtitle}
      threadId={thread.id}
      comments={comments}
      isResolved={isResolved}
      timestampLabel={formatCommentAnchorTimestamp(thread.anchor)}
      isSelected
      isUnread={isUnread(thread)}
      searchMentions={searchMentions}
      isReplying={isReplying}
      onSelect={() => undefined}
      onToggleRead={() => setThreadRead(thread.id, isUnread(thread))}
      onClose={onDismiss}
      isEmbedded={isEmbedded}
      capHeight={capHeight}
      onBack={onBack}
      onReply={(draft) => reply(thread.id, draft)}
      onEditComment={editComment}
      isSavingEdit={isSavingEdit}
      onToggleResolved={() => toggleResolved(thread.id, isResolved)}
      onDeleteComment={(commentId) => removeComment(thread.id, commentId)}
      onDeleteThread={() => removeThread(thread.id)}
      canDeleteThread={comments[0]?.canModify ?? false}
      highlightedCommentId={highlightedCommentId}
      onCopyCommentLink={(commentId) =>
        copyCommentLink({ commentId, isThreadResolved: isResolved })
      }
    />
  );
};
